import { useState, type ReactNode } from 'react';
import { devicesApi, type Project } from '../api';
import { useAuth } from '../auth';
import { useAsync } from '../hooks';
import { DeviceStateBadge, HealthBadge } from './StatusBadges';
import { Alert, Button, Field, Modal, Select, TextArea, errorText, formatDate } from './ui';

export default function DeviceModal({
  deviceId,
  projects,
  onClose,
  onChanged,
}: {
  deviceId: number;
  projects: Project[];
  onClose: () => void;
  onChanged: () => void;
}) {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const device = useAsync(() => devicesApi.get(deviceId), [deviceId]);
  const checkins = useAsync(() => devicesApi.checkins(deviceId, 50), [deviceId]);
  const [name, setName] = useState<string>();
  const [notes, setNotes] = useState<string>();
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const d = device.data;
  const project = projects.find((p) => p.id === d?.project_id);

  const run = async (action: () => Promise<unknown>, close = false) => {
    setBusy(true);
    setError(null);
    try {
      await action();
      onChanged();
      if (close) onClose();
      else {
        await device.reload();
        await checkins.reload();
      }
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title={d ? d.name || d.serial_number : 'Device'} onClose={onClose} wide>
      {device.error && <Alert kind="error">{device.error}</Alert>}
      {d && (
        <div className="space-y-5">
          {error && <Alert kind="error">{error}</Alert>}

          <dl className="grid grid-cols-2 gap-x-6 gap-y-2 text-sm sm:grid-cols-4">
            <Info label="Serial number" value={<span className="font-mono">{d.serial_number}</span>} />
            <Info label="Hardware" value={d.hardware_type} />
            <Info label="Reported version" value={<span className="font-mono">{d.reported_version ?? '—'}</span>} />
            <Info
              label="Target"
              value={
                <span className="font-mono">
                  {d.effective_version ?? '—'}
                  {d.target_source && <span className="ml-1 font-sans text-slate-400">({d.target_source === 'group' ? 'group default' : 'assigned'})</span>}
                </span>
              }
            />
            <Info label="State" value={<DeviceStateBadge state={d.state} />} />
            <Info label="Health" value={<HealthBadge health={d.health} />} />
            <Info label="Boot count" value={d.last_boot_count ?? '—'} />
            <Info label="Last reset reason" value={d.last_reset_reason ?? '—'} />
            <Info label="Last seen" value={formatDate(d.last_seen)} />
            <Info label="First seen" value={formatDate(d.first_seen)} />
            <Info label="Last IP" value={d.last_ip ?? '—'} />
            <Info label="Update attempts" value={d.update_attempts} />
          </dl>

          {d.state === 'failed' && (
            <Alert kind="warning">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <span>
                  The device was offered {d.effective_version} several times but still reports {d.reported_version}. Check that the firmware reports
                  exactly the release version.
                </span>
                <Button variant="secondary" disabled={busy} onClick={() => run(() => devicesApi.retry(d.id))}>
                  Retry update
                </Button>
              </div>
            </Alert>
          )}

          <div className="grid gap-4 sm:grid-cols-2">
            {isAdmin && (
              <Select
                label="Project"
                value={d.project_id ?? ''}
                disabled={busy}
                onChange={(e) => run(() => devicesApi.update(d.id, { project_id: e.target.value ? Number(e.target.value) : null }))}
                hint="Moving a device resets its target to the new project's default group."
              >
                <option value="">— Unassigned —</option>
                {projects
                  .filter((p) => p.hardware_type === d.hardware_type)
                  .map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.name}
                    </option>
                  ))}
              </Select>
            )}
            {project && (
              <Select
                label="Group"
                value={d.group_id ?? ''}
                disabled={busy}
                onChange={(e) => run(() => devicesApi.update(d.id, { group_id: Number(e.target.value) }))}
              >
                {project.groups.map((g) => (
                  <option key={g.id} value={g.id}>
                    {g.name}
                  </option>
                ))}
              </Select>
            )}
            <Field label="Name" value={name ?? d.name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Office reader 1" />
            <TextArea label="Notes" rows={2} value={notes ?? d.notes} onChange={(e) => setNotes(e.target.value)} />
          </div>

          <div className="flex flex-wrap justify-between gap-2">
            <div className="flex flex-wrap gap-2">
              {d.target_source === 'device' && (
                <Button
                  variant="secondary"
                  disabled={busy}
                  onClick={() => run(() => devicesApi.update(d.id, { clear_target: true }))}
                  title="Remove the assigned release; the device follows its group default again"
                >
                  Clear assigned release
                </Button>
              )}
              {isAdmin && (
                <Button
                  variant="danger"
                  disabled={busy}
                  onClick={() => confirm(`Delete device ${d.serial_number} and its history?`) && run(() => devicesApi.remove(d.id), true)}
                >
                  Delete device
                </Button>
              )}
            </div>
            <Button
              disabled={busy || (name === undefined && notes === undefined)}
              onClick={() => run(() => devicesApi.update(d.id, { name: name ?? d.name, notes: notes ?? d.notes }))}
            >
              Save name &amp; notes
            </Button>
          </div>

          <div>
            <h3 className="mb-2 text-sm font-medium">Recent check-ins</h3>
            <div className="max-h-64 overflow-auto rounded-md border border-slate-200">
              <table className="w-full text-left text-xs [&_td]:px-2 [&_td]:py-1 [&_th]:px-2 [&_th]:py-1">
                <thead className="sticky top-0 bg-slate-50 text-slate-500">
                  <tr>
                    <th>Time</th>
                    <th>Version</th>
                    <th>Boot count</th>
                    <th>Reset reason</th>
                    <th>Response</th>
                  </tr>
                </thead>
                <tbody>
                  {(checkins.data ?? []).map((c) => (
                    <tr key={c.id} className="border-t border-slate-100">
                      <td className="whitespace-nowrap">{formatDate(c.ts)}</td>
                      <td className="font-mono">{c.reported_version}</td>
                      <td>{c.boot_count ?? '—'}</td>
                      <td>{c.reset_reason ?? '—'}</td>
                      <td>{c.response.replaceAll('_', ' ')}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </Modal>
  );
}

function Info({ label, value }: { label: string; value: ReactNode }) {
  return (
    <div>
      <dt className="text-xs text-slate-500">{label}</dt>
      <dd>{value}</dd>
    </div>
  );
}
